import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useAgentStore } from '@/stores/agentStore';
import type { ChatModel } from '@/types/agent';
import { modelApi } from '@/services/modelApi';

const FALLBACK_MODELS: ChatModel[] = [
  { id: 'grsai-gemini-2.5-flash', name: 'Gemini 2.5 Flash', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-gemini-3-flash', name: 'Gemini 3 Flash', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-gemini-3.1-pro', name: 'Gemini 3.1 Pro', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-gemini-3.1-flash-lite', name: 'Gemini 3.1 Flash Lite', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-gpt-4o', name: 'GPT-4o', backend: 'grsai', supportedModes: ['chat'] },
  { id: 'grsai-deepseek-r1', name: 'DeepSeek R1', backend: 'grsai', supportedModes: ['chat'] },
];

export const AgentModelSelector: React.FC = () => {
  const { currentModelId, setCurrentModel, availableModels, setAvailableModels } = useAgentStore();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const data = await modelApi.getLlmModels();
        const chatModels: ChatModel[] = data.map((m: any) => ({
          id: m.id,
          name: m.name,
          backend: m.backend,
          supportedModes: m.supportedModes || [],
        }));
        setAvailableModels(chatModels);
      } catch {
        setAvailableModels(FALLBACK_MODELS);
      }
    };
    fetchModels();
  }, [setAvailableModels]);

  const models = availableModels.length > 0 ? availableModels : FALLBACK_MODELS;
  const currentModel = models.find((m) => m.id === currentModelId) || models[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-md transition-colors"
      >
        <span>{currentModel?.name || 'Select Model'}</span>
        <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-0' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute bottom-full left-0 mb-1 w-56 bg-[#1e1e2e] border border-white/10 rounded-lg shadow-xl z-50 py-1">
            {models.map((model) => (
              <button
                key={model.id}
                onClick={() => {
                  setCurrentModel(model.id);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                  model.id === currentModelId
                    ? 'text-white bg-white/10'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <div className="font-medium">{model.name}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">{model.backend}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
